import {getCurrentDate} from 'src/utils/dateTime';
import MemLearning from '@memrise/memlearning';
import {
  AudioMultipleChoiceTestCard as HookAudioMultipleChoiceTestCard,
  CompactLearnableProgress as HookCompactLearnableProgress,
  Correctness as HookCorrectness,
  Difficulty,
  Difficulty as HookDifficulty,
  ItemType as HookItemType,
  Learnable as HookLearnable,
  LearnableAudioValue,
  LearnableValue,
  LearnableValueKind,
  MultipleChoiceTestCard as HookMultipleChoiceTestCard,
  ResponseModel,
  Screen as HookScreen,
  SessionState as HookSessionState,
  SessionType as HookSessionType,
  TappingTestCard as HookTappingTestCard,
  TemplateName,
  Test as HookTest,
  TestPromptType,
  TestResult as HookTestResult,
  TestType as HookTestType,
  TypingTestCard as HookTypingTestCard,
  WebSessionType as HookWebSessionType,
} from 'src/api/learningSessions/types';

// Direct conversions between Hook and MemLearning objects.
// This file should have full code coverage without specific tests -
// it's tested at a higher level, e.g. in sequencer.test.ts

export const convertDifficultyToMemLearning = (
  hookDifficulty: HookDifficulty,
  // @ts-ignore: TO-DO - fix ts-ignore
): MemLearning.com.memrise.learning.models.Difficulty => {
  const lookup = {
    unknown: 'Unknown',
    hard: 'Hard',
    moderate: 'Moderate',
    easy: 'Easy',
  };
  const kotlinName = lookup[hookDifficulty];
  return MemLearning.com.memrise.foundation.makeEnum('Difficulty', kotlinName);
};

export const convertDifficultyFromMemLearning = (
  // @ts-ignore: TO-DO - fix ts-ignore
  difficulty: MemLearning.com.memrise.learning.models.Difficulty,
): HookDifficulty => {
  const lookup: Record<string, string> = {
    Unknown: 'unknown',
    Hard: 'hard',
    Moderate: 'moderate',
    Easy: 'easy',
  };
  const hookName = lookup[difficulty.toString()];
  return hookName as HookDifficulty;
};

export const convertItemTypeToMemLearning = (
  hookItemType: HookItemType | null,
  // @ts-ignore: TO-DO - fix ts-ignore
): MemLearning.com.memrise.learning.models.ItemType | null => {
  if (hookItemType === null) {
    return null;
  }
  const lookup = {
    word: 'Word',
    char: 'Character',
    phrase: 'Phrase',
    alphabet: 'Alphabet',
    romanization: 'Romanization',
    sentence: 'Sentence',
    affix: 'Affix',
    context: 'Context',
  };
  /* istanbul ignore next: internal error */
  if (!(hookItemType in lookup)) throw new Error(`Missing hookItemType: ${hookItemType}`);
  const kotlinName = lookup[hookItemType];
  return MemLearning.com.memrise.foundation.makeEnum('ItemType', kotlinName);
};

const convertDirectionToMemlearning = (
  direction: LearnableValue['direction'],
) => {
  const directionMap: Record<
    LearnableValue['direction'],
    'Target' | 'Source'
  > = {
    target: 'Target',
    source: 'Source',
  };
  return MemLearning.com.memrise.foundation.makeEnum('Direction', directionMap[direction]);
};

export const convertCorrectnessFromMemLearning = (
  // @ts-ignore: TO-DO - fix ts-ignore
  correctness: MemLearning.com.memrise.learning.evaluation.Correctness,
): HookCorrectness => {
  return correctness.toString() as HookCorrectness;
};

const convertTemplateNameToMemlearningResponseModel = (
  templateName: TemplateName,
) => {
  const lookup = {
    multiple_choice: 'MultipleChoice',
    reversed_multiple_choice: 'ReverseMultipleChoice',
    audio_multiple_choice: 'AudioMultipleChoice',
    tapping: 'Tapping',
    typing: 'Typing',
    presentation: 'Presentation',
  };
  /* istanbul ignore else: ignore error case */
  if (templateName in lookup) {
    return MemLearning.com.memrise.foundation.makeEnum(
      'CardTemplateType',
      lookup[templateName as keyof typeof lookup],
    );
  } else {
    throw new Error(
      `Cannot translate templateName of type ${templateName} to memlearning enum`,
    );
  }
};

export const isLearnableAudioValue = (
  x: unknown,
): x is LearnableAudioValue =>
  typeof x === 'object' && x !== null && 'slow' in x;

export const makeContent = ({
  label,
  direction,
  kind,
  value,
}: LearnableValue): MemLearning.com.memrise.learning.models.Content => {
  return new MemLearning.com.memrise.learning.models.Content(
    label,
    convertDirectionToMemlearning(direction),
    makeContentValue(kind, value),
  );
};

export const makeContentValue = (
  kind: LearnableValueKind,
  value: LearnableValue['value'],
): MemLearning.com.memrise.learning.models.ContentValue => {
  if (kind === LearnableValueKind.AUDIO)
    return makeAudioContentValue(value as string | LearnableAudioValue[]);

  /* istanbul ignore next: internal type check */
  if (isLearnableAudioValue(value)) {
    throw new Error('LearnableAudioValue received but kind is not audio');
  }

  // string or string[]
  const stringValue = Array.isArray(value)
    ? getNonNullValue(value as string[])
    : value;

  /* istanbul ignore next: internal type check */
  if (typeof stringValue !== 'string') {
    throw new Error(`${value} learnable value is not a string`);
  }

  switch (kind) {
    case LearnableValueKind.TEXT: {
      return new MemLearning.com.memrise.learning.models.TextContentValue(stringValue);
    }
    case LearnableValueKind.IMAGE: {
      return new MemLearning.com.memrise.learning.models.ImageContentValue(stringValue);
    }
    case LearnableValueKind.VIDEO: {
      return new MemLearning.com.memrise.learning.models.VideoContentValue(stringValue);
    }
  }
};

const getNonNullValue = (value: string[]) => {
  return value.filter(i => i !== '')[0];
};

const makeAudioContentValue = (value: string | LearnableAudioValue[]) => {
  // Each value for kind audio can be strings (URLs) or objects with
  // `normal` (string) and `slow` (string | null) properties

  // For strings, we treat the single url as the normal value and omit slow
  if (typeof value === 'string') {
    return new MemLearning.com.memrise.learning.models.AudioContentValue(
      new MemLearning.com.memrise.learning.models.AudioURL(value, null),
    );
  }

  // For an array of objects, we set the URL correctly for each speed on
  // the first value
  const [value1] = value;
  /* istanbul ignore next: Just here to check our types are sane */
  if (!isLearnableAudioValue(value1)) {
    throw new Error(`${value1} is not a LearnableAudioValue`);
  }
  return new MemLearning.com.memrise.learning.models.AudioContentValue(
    new MemLearning.com.memrise.learning.models.AudioURL(
      value1.normal,
      value1.slow || value1.normal,
    ),
  );
};

const isTest = (screen: HookScreen): screen is HookTest =>
  [
    TemplateName.MULTIPLE_CHOICE,
    TemplateName.REVERSED_MULTIPLE_CHOICE,
    TemplateName.AUDIO_MULTIPLE_CHOICE,
    TemplateName.TAPPING,
    TemplateName.TYPING,
  ].includes(screen.template);

export const getAndConvertNonTappingAnswersFromScreen = (
  screen: HookScreen,
): MemLearning.com.memrise.learning.models.Content[] => {
  /* istanbul ignore else: ignore error case */
  if (isTest(screen)) {
    const { answer, correct } = screen;
    const { kind, direction } = answer;
    const memLearningDirection = convertDirectionToMemlearning(direction);

    return correct.map((correctItem: string | string[]) => {
      /* istanbul ignore next: internal error */
      if (Array.isArray(correctItem)) {
        throw Error(
          'Tapping correct items not supported with this function, they are array of arrays',
        );
      }
      const contentValue = makeContentValue(kind, correctItem);
      return new MemLearning.com.memrise.learning.models.Content(
        '',
        memLearningDirection,
        contentValue,
      );
    });
  } else {
    /* istanbul ignore next: internal error */
    throw new Error(
      `Cannot get answers from screen template of type ${screen.template}.`,
    );
  }
};

export const getAndConvertTappingAnswersFromScreen = (
  screen: HookScreen,
  // @ts-ignore: TO-DO - fix ts-ignore
): Collections.List<Collections.List<string>> => {
  /* istanbul ignore next: internal error */
  if (!isTest(screen)) {
    throw Error('Can only convert answers on a test type');
  }
  const { correct } = screen;
  const answerSegments = correct
    // XXX: filter out empty answer arrays
    // These should not be sent by the backend, but they are and this seems to
    // cause issues with KotlinJs conversions

    // Filter out empty array answers while leaving non-array items untouched
    .filter(item => !Array.isArray(item) || item.length > 0)
    .map((correctItem: string | string[]) => {
      /* istanbul ignore next: internal error */
      if (!Array.isArray(correctItem)) {
        throw Error('Tapping test correct values are arrays of arrays');
      }
      return MemLearning.com.memrise.foundation.makeList(correctItem);
    });
  return MemLearning.com.memrise.foundation.makeList(answerSegments);
};

const getAndConvertDistractorsFromScreen = (
  screen: HookScreen,
): MemLearning.com.memrise.learning.models.Content[] => {
  /* istanbul ignore else: ignore error case */
  if (isTest(screen)) {
    const { answer, choices } = screen;
    const direction = convertDirectionToMemlearning(answer.direction);
    const { kind } = answer;
    return choices.map(value => {
      const contentValue = makeContentValue(kind, value);
      return new MemLearning.com.memrise.learning.models.Content('', direction, contentValue);
    });
  } else {
    throw new Error(
      `Screen template of type ${screen.template} not supported for distractor generation. You are either incorrectly trying to generate distractors for this test type or you need to implement distractor generation for this test type.`,
    );
  }
};

const getAndConvertKeyboardFromScreen = (screen: HookScreen): string[] => {
  /* istanbul ignore else: ignore error case */
  if (isTest(screen)) {
    const { answer, choices } = screen;
    const { kind } = answer;
    /* istanbul ignore next: this shouldn't occur in screen data */
    if (kind !== LearnableValueKind.TEXT) {
      throw new Error('Typing tests can only be done on text');
    }
    return choices;
  } else {
    throw new Error(
      `Screen template of type ${screen.template} not supported for keyboard generation.`,
    );
  }
};

type PromptKind = 'text' | 'audio' | 'video' | 'image';
const makeContentKind = (
  kind: PromptKind,
  // @ts-ignore: TO-DO - fix ts-ignore
): MemLearning.com.memrise.learning.models.ContentKind => {
  const lookup: Record<PromptKind, string> = {
    text: 'Text',
    audio: 'Audio',
    video: 'Video',
    image: 'Image',
  };
  return MemLearning.com.memrise.foundation.makeEnum('ContentKind', lookup[kind]);
};

const getAndConvertPromptsFromScreen = (
  screen: HookScreen,
): [
  // @ts-ignore: TO-DO - fix ts-ignore
  MemLearning.com.memrise.learning.models.ContentKind,
  // @ts-ignore: TO-DO - fix ts-ignore
  Collections.List<MemLearning.com.memrise.learning.models.Content>,
][] => {
  /* istanbul ignore else: ignore error case */
  if (isTest(screen)) {
    /* istanbul ignore next: ignore error case */
    if (!screen.prompt) throw new Error('could not get prompt');
    return Object.entries(screen.prompt)
      .filter(([, learnableValue]) => learnableValue !== null)
      .map(([kind, learnableValue]) => [
        makeContentKind(kind as PromptKind),
        MemLearning.com.memrise.foundation.makeList([
          new MemLearning.com.memrise.learning.models.Content(
            '',
            convertDirectionToMemlearning(learnableValue.direction),
            makeContentValue(
              kind as LearnableValueKind,
              learnableValue.value,
            ),
          ),
        ]),
      ]);
  } else {
    throw new Error(
      `Screen template of type ${screen.template} not supported yet. You may have more implementation work to do.`,
    );
  }
};

const supportedTemplateNames: TemplateName[] = [
  TemplateName.MULTIPLE_CHOICE,
  TemplateName.REVERSED_MULTIPLE_CHOICE,
  TemplateName.AUDIO_MULTIPLE_CHOICE,
  TemplateName.TYPING,
  TemplateName.TAPPING,
  TemplateName.PRESENTATION,
];
const isSupportedScreenType = (screen: HookScreen) =>
  supportedTemplateNames.includes(screen.template);

const convertScreenToMemlearningTemplate = (
  screen: HookScreen,
  // @ts-ignore: TO-DO - fix ts-ignore
): MemLearning.com.memrise.learning.models.CardTemplate => {
  if (screen.template === TemplateName.PRESENTATION) {
    return new MemLearning.com.memrise.learning.models.PresentationCardTemplate(
      makeContent(screen.item),
      makeContent(screen.definition),
      MemLearning.com.memrise.foundation.makeList(
        [...screen.visibleInfo, ...screen.hiddenInfo].map(info =>
          makeContent(info),
        ),
      ),
      MemLearning.com.memrise.foundation.makeList([]),
      MemLearning.com.memrise.foundation.makeList([]),
      screen.nugget ?? null,
    );
  }

  const prompts = MemLearning.com.memrise.foundation.makeMap(
    getAndConvertPromptsFromScreen(screen),
  );

  // Cast as string necessary for default
  switch (screen.template as string) {
    case TemplateName.TYPING:
      return new MemLearning.com.memrise.learning.models.TypingCardTemplate(
        prompts,
        MemLearning.com.memrise.foundation.makeList(
          getAndConvertNonTappingAnswersFromScreen(screen),
        ),
        MemLearning.com.memrise.foundation.makeList(getAndConvertKeyboardFromScreen(screen)), // keyboard
        screen.isStrict,
        screen.answer.label === 'Romaji' ||
        screen.answer.label === 'Pinyin',
        MemLearning.com.memrise.foundation.makeList([]),
        MemLearning.com.memrise.foundation.makeList([]),
      );
    case TemplateName.TAPPING:
      return new MemLearning.com.memrise.learning.models.TappingCardTemplate(
        prompts,
        getAndConvertTappingAnswersFromScreen(screen),
        MemLearning.com.memrise.foundation.makeList((screen as HookTest).choices),
        screen.isStrict,
        MemLearning.com.memrise.foundation.makeList([]),
        MemLearning.com.memrise.foundation.makeList([]),
      );
    case TemplateName.MULTIPLE_CHOICE:
    case TemplateName.REVERSED_MULTIPLE_CHOICE:
      return new MemLearning.com.memrise.learning.models.MultipleChoiceCardTemplate(
        prompts,
        MemLearning.com.memrise.foundation.makeList(
          getAndConvertNonTappingAnswersFromScreen(screen),
        ),
        MemLearning.com.memrise.foundation.makeList(getAndConvertDistractorsFromScreen(screen)),
        MemLearning.com.memrise.foundation.makeList([]),
        MemLearning.com.memrise.foundation.makeList([]),
      );
    case TemplateName.AUDIO_MULTIPLE_CHOICE:
      return new MemLearning.com.memrise.learning.models.AudioMultipleChoiceCardTemplate(
        prompts,
        MemLearning.com.memrise.foundation.makeList(
          getAndConvertNonTappingAnswersFromScreen(screen).map(
            content => content.value,
          ),
        ),
        MemLearning.com.memrise.foundation.makeList(
          getAndConvertDistractorsFromScreen(screen).map(
            content => content.value,
          ),
        ),
        MemLearning.com.memrise.foundation.makeList([]),
        MemLearning.com.memrise.foundation.makeList([]),
      );
    /* istanbul ignore next */
    default:
      throw new Error(
        `Tried to convert a ${screen.template} screen but this case has not been handled yet.`,
      );
  }
};

export const convertScreensToMemlearningTemplates = (
  screens: HookScreen[],
  forceTestType?: TemplateName | null,
  // @ts-ignore: TO-DO - fix ts-ignore
): [MemLearning.com.memrise.learning.models.CardTemplateType, MemLearning.com.memrise.learning.models.CardTemplate][] =>
  screens
    .filter(isSupportedScreenType)
    .filter((item) => {
      /* istanbul ignore if : dev tools only */
      if (forceTestType) {
        return item.template === forceTestType || item.template === TemplateName.MULTIPLE_CHOICE;
      }
      return item;
    })
    .map(screen => [
      convertTemplateNameToMemlearningResponseModel(screen.template),
      convertScreenToMemlearningTemplate(screen),
    ]);

const convertScreensToTemplates = (
  screens: HookScreen[],
  forceTestType?: TemplateName | null,
  // @ts-ignore: TO-DO - fix ts-ignore
): Collections.Map<
  // @ts-ignore: TO-DO - fix ts-ignore
  MemLearning.com.memrise.learning.models.CardTemplateType,
  // @ts-ignore: TO-DO - fix ts-ignore
  MemLearning.com.memrise.learning.models.CardTemplate
> => MemLearning.com.memrise.foundation.makeMap(convertScreensToMemlearningTemplates(screens, forceTestType));

export const convertLearnableToMemLearning = (
  hookLearnable: HookLearnable,
  forceTestType?: TemplateName | null,
): MemLearning.com.memrise.learning.models.Learnable =>
  new MemLearning.com.memrise.learning.models.Learnable(
    hookLearnable.id.toString(),
    convertItemTypeToMemLearning(hookLearnable.itemType),
    hookLearnable.learningElement,
    hookLearnable.learningElementTokens,
    hookLearnable.definitionElement,
    hookLearnable.definitionElementTokens,
    convertDifficultyToMemLearning(hookLearnable.difficulty),
    convertScreensToTemplates(hookLearnable.screens, forceTestType),
  );

// See src/commonMain/kotlin/com/memrise/learning/session/SessionType.kt in memlearning for values
const sessionTypeToMemLearning: Record<HookWebSessionType, string> = {
  [HookSessionType.SPEED_REVIEW]: 'SpeedReview',
  [HookSessionType.VIDEO]: 'VideoReview',
  [HookSessionType.AUDIO]: 'Audio',
  [HookSessionType.REVIEW]: 'Review',
  [HookSessionType.REVIEW_ALEX]: 'Review',
  [HookSessionType.LEARN]: 'Learn',
  [HookSessionType.DIFFICULT_WORDS]: 'DifficultWords',
  [HookSessionType.MIXED]: 'Mixed',
};

export const convertSessionTypeToMemLearning = (
  hookSessionType: HookWebSessionType,
  // @ts-ignore: TO-DO - fix ts-ignore
): MemLearning.com.memrise.learning.session.SessionType => {
  const kotlinName = sessionTypeToMemLearning[hookSessionType];
  return MemLearning.com.memrise.foundation.makeEnum('SessionType', kotlinName);
};

const makeDefaultLearnableProgress = () =>
  new MemLearning.com.memrise.learning.models.LearnableProgress(
    '0',
    0,
    0,
    0,
    0,
    0,
    convertDateUTCStringToMemLearning(getCurrentDate().toUTCString()),
    null,
    null,
    0.01,
    false, // starred
    true, // notDifficult
    false, // markedAsKnown
  );

export const convertDateToMemLearning = (date: Date) => {
  // Note: this looses precision as we have to round milliseconds to seconds
  const parsedDateInSeconds = Math.round(
    Date.parse(date.toUTCString()) / 1000,
  );
  return new MemLearning.com.memrise.foundation.DateTime(parsedDateInSeconds);
};

export const convertDateFromMemLearning = (
  date: MemLearning.com.memrise.foundation.DateTime,
): Date => {
  const memlearningDateInMilliseconds = date.secondsSince1970 * 1000;
  return new Date(memlearningDateInMilliseconds);
};

export const convertDateUTCStringToMemLearning = (dateString: string) => {
  const parsedDateInMilliseconds = Date.parse(dateString);
  return convertDateToMemLearning(new Date(parsedDateInMilliseconds));
};

export const convertProgressToMemLearning = (
  progress: HookCompactLearnableProgress | null,
): MemLearning.com.memrise.learning.models.LearnableProgress => {
  if (!progress) return makeDefaultLearnableProgress();
  const {
    learnableId,
    growthLevel,
    attempts,
    correct,
    currentStreak,
    totalStreak,
    createdDate,
    lastDate,
    nextDate,
    interval,
    notDifficult,
    starred,
    ignored,
  } = progress;
  return new MemLearning.com.memrise.learning.models.LearnableProgress(
    learnableId.toString(),
    growthLevel,
    attempts,
    correct,
    currentStreak,
    totalStreak,
    convertDateUTCStringToMemLearning(createdDate),
    lastDate ? convertDateUTCStringToMemLearning(lastDate) : null,
    nextDate ? convertDateUTCStringToMemLearning(nextDate) : null,
    interval,
    starred,
    notDifficult,
    ignored,
  );
};

export const convertProgressFromMemLearning = (
  {
    growthLevel,
    attempts,
    correct,
    currentStreak,
    totalStreak,
    createdDate,
    lastDate,
    nextDate,
    interval,
    starred,
    notDifficult,
    markedAsKnown,
  }: MemLearning.com.memrise.learning.models.LearnableProgress,
  learnableId: string,
): HookCompactLearnableProgress => {
  return {
    learnableId: parseInt(learnableId),
    growthLevel,
    attempts,
    correct,
    currentStreak,
    totalStreak,
    // @ts-ignore: TO-DO - fix ts-ignore
    interval,
    createdDate: convertDateFromMemLearning(createdDate).toISOString(),
    lastDate: lastDate
      ? convertDateFromMemLearning(lastDate).toISOString()
      : /* istanbul ignore next: untested branch of code, please test */ null,
    nextDate: nextDate
      ? convertDateFromMemLearning(nextDate).toISOString()
      : null,
    starred,
    notDifficult,
    ignored: markedAsKnown,
  };
};

export const convertLearnablesWithProgressToMemLearning = (
  learnablesWithProgress: {
    learnable: HookLearnable;
    progress: HookCompactLearnableProgress | null;
  }[],
  forceTestType?: TemplateName | null,
  // @ts-ignore: TO-DO - fix ts-ignore
): Collections.List<MemLearning.com.memrise.learning.models.LearnableWithProgress> => {
  return MemLearning.com.memrise.foundation.makeList(
    learnablesWithProgress.map(learnableWithProgress => {
      const memLearningLearnable = convertLearnableToMemLearning(
        learnableWithProgress.learnable,
        forceTestType,
      );
      const memLearningProgress = convertProgressToMemLearning(
        learnableWithProgress.progress,
      );
      return new MemLearning.com.memrise.learning.models.LearnableWithProgress(
        memLearningLearnable,
        memLearningProgress,
      );
    }),
  );
};

export const convertTestResultFromMemLearning = (
  testResult: MemLearning.com.memrise.learning.models.TestResult,
): HookTestResult => {
  const totalPoints = testResult.points.mj_1 + testResult.points.nj_1; // answerPoints + streakPoints

  /* istanbul ignore next */
  if (totalPoints < 0) {
    throw Error('Test points has returned negative');
  }

  return {
    learnableProgress: convertProgressFromMemLearning(
      testResult.learnableWithProgress.progress,
      testResult.learnableWithProgress.learnable.identifier,
    ),
    isDifficult: testResult.learnableWithProgress.progress.isDifficult(),
    points: totalPoints || 0,
    correctness: convertCorrectnessFromMemLearning(testResult.correctness),
    totalSessionPoints: testResult.totalSessionPoints,
  };
};

export const convertCardFromMemLearning = (
  card: MemLearning.com.memrise.learning.session.LearnableCard,
  sessionProgress: MemLearning.com.memrise.learning.models.SessionProgress,
): Partial<HookSessionState> => {
  const sessionPercentComplete =
    ((sessionProgress.indexOfCurrentCard - 1) /
      sessionProgress.totalNumberCards) *
    100;
  const sessionItemCount = sessionProgress.totalNumberCards;
  /* istanbul ignore else: only fails on internal logic error */
  if (card instanceof MemLearning.com.memrise.learning.session.cards.MultimediaPresentationCard) {
    const learnableId = card.learnableWithProgress.learnable.identifier;
    /* istanbul ignore next: function overwritten in useSessionStore */
    const onContinue = () => { };
    const { growthLevel } = card.learnableWithProgress.progress;
    return {
      kind: 'CardState',
      card: {
        kind: 'PresentationCard',
        learnableId,
        onContinue,
        previousIncorrectAnswer: card.previousIncorrectAnswer as string | null,
      },
      growthLevel,
      sessionPercentComplete,
      sessionItemCount,
    };
  }

  const testCard = card as MemLearning.com.memrise.learning.session.TestCard;
  const learnableId = card.learnableWithProgress.learnable.identifier;

  const partialHookCard = {
    kind: 'TestCard',
    learnableId,
    testType: convertTestTypeFromMemLearning(testCard.testType),
  };

  let hookCard;

  // Due to the way memlearning and web client have evolved, we don't yet use the
  // answer/choices from the multiple choice or typing cards. We should eventually
  // use these, but we don't need to refactor straight away.
  // prettier-ignore
  // Because of the istanbul ignore between else..if
  if (card instanceof MemLearning.com.memrise.learning.session.cards.MultipleChoiceTestCard) {
    // Nothing to add at the moment - see comment above about needed refactor
    hookCard = partialHookCard as HookMultipleChoiceTestCard;
    hookCard.choices = card.choices.q2_1.map(
      (choice: MemLearning.com.memrise.learning.models.ContentValue) => choice.stringValue,
    );
    hookCard.answer = card.answer.value.stringValue;
    hookCard.responseType = card.answer.value.kind.toString();
  } else if (card instanceof MemLearning.com.memrise.learning.session.cards.TypingTestCard) {
    hookCard = partialHookCard as HookTypingTestCard;

    hookCard.isCopy = testCard.testType.isCopy;
  } else if (card instanceof MemLearning.com.memrise.learning.session.cards.TappingTestCard) {
    hookCard = partialHookCard as HookTappingTestCard;
    hookCard.answers = card.answers.q2_1[0].q2_1;
    hookCard.choices = card.choices.q2_1;

    hookCard.isCopy = testCard.testType.isCopy;
  } else /* istanbul ignore else: untested line of code, please test */ if (
    card instanceof MemLearning.com.memrise.learning.session.cards.AudioMultipleChoiceTestCard
  ) {
    hookCard = partialHookCard as HookAudioMultipleChoiceTestCard;
    hookCard.answer = card.answer;
    hookCard.choices = card.choices.q2_1;
  } else {
    throw new Error('Unknown Card type');
  }

  const { growthLevel } = card.learnableWithProgress.progress;
  return {
    kind: 'CardState',
    card: hookCard,
    sessionPercentComplete,
    sessionItemCount,
    growthLevel,
  };
};

export const convertTestTypeFromMemLearning = (
  testType: MemLearning.com.memrise.learning.tests.TestType,
): HookTestType => {
  const difficultyEnum = testType.difficulty;
  const difficulty = difficultyEnum ? difficultyEnum.toString().toLowerCase() as Difficulty : null;

  return {
    model: testType.model.toString() as ResponseModel,
    prompt: testType.prompt.toString() as TestPromptType,
    difficulty,
    isCopy: testType.isCopy,
    isFlipped: testType.isFlipped,
    weight: testType?.weight,
    hasMultimedia: testType.hasMultimedia,
  };
};

export const convertMemLearningTypingCardToAnswers = (
  card: MemLearning.com.memrise.learning.session.cards.TypingTestCard,
): string[] => {
  const contentArray: MemLearning.com.memrise.learning.models.Content[] =
    card.answers.q2_1;
  return contentArray.map(content => content.value.stringValue);
};

export const convertMemLearningTappingCardToAnswers = (
  card: MemLearning.com.memrise.learning.session.cards.TappingTestCard,
): string[] => {
  // @ts-ignore: TO-DO - fix ts-ignore
  const separateAnswersArray: MemLearning.com.memrise.learning.session.cards.TappingTestCard.answers[] = Object.values(card.answers);

  return (
    separateAnswersArray
      // XXX: Filter out undefined answer arrays to avoid calling ._array on undefined
      // This is caused by the backend sending empty answer arrays and kotlinjs converting badly
      .filter((answersArray) => !!answersArray).map(answersArray => {
        const result: string[] = [];

        answersArray?.forEach((v: string[][]) => {
          const value: string[] = Object.values(v)[1];

          if (Array.isArray(Object.values(v)) && value) {
            result.push(value.join(' '));
          }
        });

        return result;
    }).flatMap(v => v)
  );
};

export type PresentSummary = (state: {
  endOfSessionPoints: number;
}) => void;

export const convertMemlearningStateToDomain = (
  state: MemLearning.com.memrise.learning.session.SessionState,
  presentSummary: PresentSummary,
): Partial<HookSessionState | undefined> => {
  if (state.stage === MemLearning.com.memrise.learning.session.InitialStage) {
    return { kind: 'LoadingState' };
  }
  if (state.stage instanceof MemLearning.com.memrise.learning.session.CardViewedStage) {
    const card = state.stage.card;
    return convertCardFromMemLearning(card as MemLearning.com.memrise.learning.session.LearnableCard, state.stage.sessionProgress);
  }
  if (state.stage instanceof MemLearning.com.memrise.learning.session.SummaryStage) {
    const endOfSessionPoints = state.stage.summaryStats.bonusPoints;
    presentSummary({ endOfSessionPoints });
    return undefined;
  }
};
